import { createContext, useContext, useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { useTipHandlers } from "./tip-handlers.ts";
import type { TipController } from "./tips.ts";

/**
 * The page's tooltip controller for the controls that carry words of their own (P2.8-T68, ADR-061), provided by the HUD
 * and read by `HoverWords`; null where nothing shows tooltips.
 */
export const TipsContext = createContext<TipController | null>(null);

/**
 * The next anchor number a `HoverWords` takes; each wrapper keeps the one it was given for as long as it lives.
 */
let nextWordsAnchor = 0;

/**
 * The `data-tip` attribute of a `HoverWords` wrapper.
 *
 * @param index The wrapper's anchor number.
 * @returns The anchor, as `words:3`.
 * @example
 * wordsTipAnchor(3);
 * // "words:3"
 */
export function wordsTipAnchor(index: number): string {
  return `words:${index}`;
}

/**
 * The words a `HoverWords` wrapper shows, read from the page.
 *
 * @param index The wrapper's anchor number.
 * @returns The words, or null where the wrapper is not drawn or has none.
 */
export function wordsOf(index: number): string | null {
  if (typeof document === "undefined") {
    return null;
  }
  const words = document.querySelector(`[data-tip="${wordsTipAnchor(index)}"]`)?.getAttribute("data-tip-words") ?? null;
  return words === null || words === "" ? null : words;
}

/**
 * Words for a control in the HUD's tooltip, in place of a native `title` (ADR-061): the wrapper is one anchor of the
 * page's `TipController`, so the hover delay, keyboard focus, a long press, a press elsewhere and Escape are the bar's own,
 * and `HudTooltip` draws the words in the HUD's layer beside the measured wrapper, held inside the area and clipped by
 * no window. The words are never the only copy: the wrapped control carries its own accessible name. Where no
 * controller is provided it draws its children alone.
 *
 * @param props The `words` shown; the wrapped `children`; `block`, whether the wrapper fills its row; `pin`, whether a
 *   click or a tap holds the words open until a press elsewhere, for a child a click does nothing on (a name, a badge),
 *   which is also given a tab stop so a keyboard reaches its words; a child with an action is not pinned by its click.
 * @returns The wrapper.
 * @example
 * <HoverWords words="Pop out"><GlyphButton glyph="popout" label="Pop out" size={16} onClick={open} /></HoverWords>
 */
export function HoverWords({ words, children, block = false, pin = false }: { readonly words: string; readonly children: ReactNode; readonly block?: boolean; readonly pin?: boolean }): ReactElement {
  const tips = useContext(TipsContext);
  const handlers = useTipHandlers(tips);
  const index = useRef<number | null>(null);
  if (index.current === null) {
    index.current = nextWordsAnchor;
    nextWordsAnchor += 1;
  }
  const own = index.current;
  useEffect(() => {
    tips?.refresh({ surface: "words", index: own });
  }, [tips, own, words]);
  if (tips === null || handlers === null) {
    return <>{children}</>;
  }
  return (
    <span className={block ? "ot-hover-words ot-hover-words--block" : "ot-hover-words"} data-kit="hover-words" data-tip={wordsTipAnchor(index.current)} data-tip-words={words} data-tip-pin={pin ? "" : undefined} tabIndex={pin ? 0 : undefined} {...handlers}>
      {children}
    </span>
  );
}
